import { areBodiesInSameKinematicChain } from "./bodyHierarchy";
import { nextConstraintName } from "./constraintNames";
import { namesFromRecord, nextUniqueName } from "./uniqueNames";
import { stripNamespacePrefix as stripPrefix } from "./nameUtils";

const firstDifferent = (ids, preferred) => ids.find((id) => id && id !== preferred) || null;
const isScalarJoint = (joint) => joint?.kind === "hinge" || joint?.kind === "slide";

export function addDefaultBodyConstraint(
  sceneActions,
  bodies,
  preferredBodyUuid = null,
  type = "connect",
  equalities = {}
) {
  const bodyIds = Object.values(bodies || {})
    .map((body) => body?.uuid)
    .filter(Boolean);
  const obj1 = preferredBodyUuid || bodyIds[0] || null;
  const obj2 = bodyIds.find(
    (id) => id && id !== obj1 && !areBodiesInSameKinematicChain(bodies || {}, obj1, id)
  );
  if (!sceneActions?.addEquality || !obj1 || !obj2) return null;
  return sceneActions.addEquality({
    name: nextConstraintName(equalities),
    type,
    objtype: "body",
    obj1,
    obj2,
    data: type === "connect" ? [0, 0, 0] : undefined,
    solref: [0.02, 2],
    solimp: [0.9, 0.99, 0.001, 0.5, 2],
    active: true,
  });
}

export function addDefaultJointConstraint(
  sceneActions,
  joints,
  preferredJointUuid = null,
  equalities = {}
) {
  const scalarJoints = Object.values(joints || {}).filter(isScalarJoint);
  const scalarIds = scalarJoints.map((joint) => joint?.uuid).filter(Boolean);
  const obj1 = scalarIds.includes(preferredJointUuid) ? preferredJointUuid : scalarIds[0] || null;
  const obj2 = firstDifferent(scalarIds, obj1);
  if (!sceneActions?.addEquality || !obj1 || !obj2) return null;
  return sceneActions.addEquality({
    name: nextConstraintName(equalities),
    type: "joint",
    objtype: "joint",
    obj1,
    obj2,
    data: [0, 1, 0, 0, 0],
    solref: [0.02, 2],
    solimp: [0.9, 0.99, 0.001, 0.5, 2],
    active: true,
  });
}

export function addDefaultTendon(sceneActions, joints, preferredJointUuid = null, tendons = {}) {
  const scalarJoints = Object.values(joints || {}).filter(isScalarJoint);
  const scalarIds = scalarJoints.map((joint) => joint?.uuid).filter(Boolean);
  const firstJoint = scalarIds.includes(preferredJointUuid)
    ? preferredJointUuid
    : scalarIds[0] || null;
  if (!sceneActions?.addTendon || !firstJoint) return null;
  const secondJoint = firstDifferent(scalarIds, firstJoint);
  const firstJointName = stripPrefix(joints?.[firstJoint]?.name) || "joint";
  const members = [{ joint: firstJoint, coef: 1 }];
  if (secondJoint) members.push({ joint: secondJoint, coef: 1 });
  return sceneActions.addTendon({
    name: nextUniqueName(`${firstJointName}_tendon`, namesFromRecord(tendons)),
    kind: "fixed",
    joints: members,
  });
}
